import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { MultiSelectProps } from 'carbon-react/lib/components/select';

import { Organisation } from '@futrli-sdk/organisations';
import { Role } from '@futrli-sdk/roles';
import { validateEmailAddress } from '@futrli/shared-services';
import { WizardFormErrors } from '@futrli/ui-components/components/sidebar/wizard/types';

import { MAX_USER_FULL_NAME_LENGTH, USER_ROLE_IDS } from '../constants';
import { UserFormController, UserFormValues } from '../types';

interface UseUserFormOptions {
  initialValues?: Partial<UserFormValues>;
  organisations: Organisation[];
  roles: Role[];
}

export const useUserForm = ({ initialValues, organisations, roles }: UseUserFormOptions): UserFormController => {
  const [email, setEmail] = useState(initialValues?.email ?? '');
  const [errors, setErrors] = useState<WizardFormErrors>({});
  const [fullName, setFullName] = useState(initialValues?.fullName ?? '');
  const [roleId, setRoleId] = useState(initialValues?.roleId ?? '');
  const [selectedOrganisations, setSelectedOrganisations] = useState<Record<string, boolean>>(
    initialValues?.selectedOrganisations ?? {}
  );
  const hasBeenValidated = useRef(false);
  const initialValuesSignature = JSON.stringify(initialValues);
  const previousInitialValuesSignature = useRef(initialValuesSignature);
  const intl = useIntl();

  useEffect(() => {
    if (initialValues && initialValuesSignature !== previousInitialValuesSignature.current) {
      setEmail(initialValues.email ?? '');
      setFullName(initialValues.fullName ?? '');
      setRoleId(initialValues.roleId ?? '');
      setSelectedOrganisations(initialValues.selectedOrganisations ?? {});
      setErrors({});
      hasBeenValidated.current = false;
    }
    previousInitialValuesSignature.current = initialValuesSignature;
  }, [initialValues, initialValuesSignature]);

  useEffect(() => {
    if (!organisations.length) {
      return;
    }
    setSelectedOrganisations(current =>
      organisations.reduce<Record<string, boolean>>((selection, organisation) => {
        selection[organisation.id] = current[organisation.id] ?? false;
        return selection;
      }, {})
    );
  }, [organisations]);

  const validate = useCallback(() => {
    const newErrors: WizardFormErrors = {};
    const trimmedFullName = fullName.trim();

    if (!trimmedFullName) {
      newErrors.fullName = {
        id: 'user-full-name',
        message: intl.formatMessage({
          id: 'predict.workspaces.users.invite.validation.full_name_required',
          defaultMessage: 'Full name is required'
        })
      };
    } else if (trimmedFullName.length > MAX_USER_FULL_NAME_LENGTH) {
      newErrors.fullName = {
        id: 'user-full-name',
        message: intl.formatMessage(
          {
            id: 'common_max_length',
            defaultMessage: 'Maximum length is {max} characters.'
          },
          { max: MAX_USER_FULL_NAME_LENGTH }
        )
      };
    }

    if (!email.trim()) {
      newErrors.email = {
        id: 'user-email',
        message: intl.formatMessage({
          id: 'predict.workspaces.users.invite.validation.email_required',
          defaultMessage: 'Email address is required'
        })
      };
    } else if (!validateEmailAddress(email.trim())) {
      newErrors.email = {
        id: 'user-email',
        message: intl.formatMessage({
          id: 'predict.workspaces.users.invite.validation.email_invalid',
          defaultMessage: 'Enter a valid email address'
        })
      };
    }

    if (!roleId) {
      newErrors.role = {
        id: 'user-role',
        message: intl.formatMessage({
          id: 'predict.workspaces.users.invite.validation.role_required',
          defaultMessage: 'Role is required'
        })
      };
    }

    const hasSelectedOrganisation = organisations.some(organisation => selectedOrganisations[organisation.id]);
    if (roleId !== USER_ROLE_IDS.SUPER_ADMIN && !hasSelectedOrganisation) {
      newErrors.organisations = {
        id: 'user-organisations',
        message: intl.formatMessage({
          id: 'predict.workspaces.users.invite.validation.organisation_required',
          defaultMessage: 'Select at least one organisation'
        })
      };
    }

    setErrors(newErrors);
    hasBeenValidated.current = true;
    return Object.keys(newErrors).length === 0;
  }, [email, fullName, intl, organisations, roleId, selectedOrganisations]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  const onChangeEmail = useCallback((event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value), []);
  const onChangeFullName = useCallback((event: ChangeEvent<HTMLInputElement>) => setFullName(event.target.value), []);
  const onChangeOrganisations: MultiSelectProps['onChange'] = useCallback(
    event => {
      const selectedIds = ((event.target as unknown) as { value: string[] }).value;
      setSelectedOrganisations(
        organisations.reduce<Record<string, boolean>>((selection, organisation) => {
          selection[organisation.id] = selectedIds.includes(organisation.id);
          return selection;
        }, {})
      );
    },
    [organisations]
  );
  const onChangeRole = useCallback((event: ChangeEvent<HTMLInputElement>) => setRoleId(event.target.value), []);

  const selectAllOrganisations = useCallback(() => {
    setSelectedOrganisations(
      organisations.reduce<Record<string, boolean>>((selection, organisation) => {
        selection[organisation.id] = true;
        return selection;
      }, {})
    );
  }, [organisations]);

  const deselectAllOrganisations = useCallback(() => {
    setSelectedOrganisations(
      organisations.reduce<Record<string, boolean>>((selection, organisation) => {
        selection[organisation.id] = false;
        return selection;
      }, {})
    );
  }, [organisations]);

  return {
    deselectAllOrganisations,
    email,
    errors,
    fullName,
    onChangeEmail,
    onChangeFullName,
    onChangeOrganisations,
    onChangeRole,
    organisations,
    revalidate,
    roleId,
    roles,
    selectAllOrganisations,
    selectedOrganisations,
    validate
  };
};
